import TokenService from "./token.service.js";
import refreshTokens from "./authRefresh.js";

/**
 * Shared auth interceptors for our axios instances (api.js, contentApi.js).
 * Previously each file carried its own copy with three bugs: no _retry guard
 * (401 loops), the rotated refresh token was never stored, and non-401 /
 * no-response branches fell through returning undefined, so failed requests
 * RESOLVED with undefined instead of rejecting.
 */
export default function setupInterceptors(instance) {
  instance.interceptors.request.use(
    (config) => {
      const token = TokenService.getLocalAccessToken();
      if (token) {
        config.headers["Authorization"] = "Bearer " + token;
      }
      // No Accept-Language header: the platform is single-language and nothing
      // initializes i18next, so localStorage's i18nextLng was always null and
      // the header was never actually sent. If multi-language UI ever lands,
      // set the header here from the active i18next language.
      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalConfig = error?.config;

      // Network error / timeout: nothing to recover, surface it.
      if (!error?.response) {
        return Promise.reject(error);
      }

      const url = originalConfig?.url || "";
      if (
        error.response.status === 401 &&
        originalConfig &&
        !originalConfig._retry &&
        !url.includes("/auth/login") &&
        !url.includes("/auth/refresh")
      ) {
        originalConfig._retry = true;
        try {
          await refreshTokens();
          return instance(originalConfig);
        } catch (refreshError) {
          // Refresh rejected: the session was replaced by a login on another
          // device (single-device policy) or the refresh token expired.
          TokenService.removeTokens();
          // Tell the React tree the session ended. The interceptor lives
          // outside React, so without this the app kept rendering the
          // protected page with stale logged-in state until a manual reload;
          // AuthContext listens and redirects to /login.
          window.dispatchEvent(new CustomEvent("auth:session-expired"));
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
}
